import { useState, useEffect, FormEvent } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { PublicHeader } from "../components/Header";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { ErrorBanner } from "../components/ErrorBanner";
import { ConsentModal } from "../components/ConsentModal";
import { checkCode, register as apiRegister } from "../api/auth";
import type { CheckCodeOut } from "../api/auth";
import { setParentJwt } from "../api/client";

export function RegisterPage() {
  const params = useParams(); // /r/:code
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const codeFromUrl = (params.code || searchParams.get("code") || "").toUpperCase();

  // Step 1: code check
  const [code, setCode] = useState(codeFromUrl);
  const [studentInfo, setStudentInfo] = useState<CheckCodeOut | null>(null);
  const [step, setStep] = useState<1 | 2>(1);

  // Step 2: form
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [phone, setPhone] = useState("");
  const [consentPdn, setConsentPdn] = useState(false);
  const [consentModalOpen, setConsentModalOpen] = useState(false);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function performCheckCode(c: string) {
    setError("");
    setLoading(true);
    try {
      const info = await checkCode({ code: c.toUpperCase() });
      setStudentInfo(info);
      setCode(c.toUpperCase());
      setStep(2);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string | unknown } } };
      const msg = e?.response?.data?.detail;
      setStudentInfo(null);
      setError(
        typeof msg === "string"
          ? msg
          : "Код не найден, истёк или уже использован"
      );
    } finally {
      setLoading(false);
    }
  }

  // Auto-check if code in URL
  useEffect(() => {
    if (codeFromUrl && !studentInfo) {
      performCheckCode(codeFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeFromUrl]);

  async function onRegister(e: FormEvent) {
    e.preventDefault();
    setError("");

    if (password !== passwordConfirm) {
      setError("Пароли не совпадают");
      return;
    }
    if (password.length < 8) {
      setError("Пароль должен быть не короче 8 символов");
      return;
    }
    if (!/\d/.test(password)) {
      setError("Пароль должен содержать хотя бы одну цифру");
      return;
    }
    if (!consentPdn) {
      setError("Для регистрации требуется согласие на обработку ПДн");
      return;
    }

    setLoading(true);
    try {
      const res = await apiRegister({
        code,
        email,
        password,
        display_name: displayName,
        phone: phone || undefined,
        consent_pdn: true,
      });
      setParentJwt(res.token);
      navigate("/cabinet");
    } catch (err: unknown) {
      const ex = err as { response?: { data?: { detail?: string | unknown } } };
      const msg = ex?.response?.data?.detail;
      if (typeof msg === "string" && msg.includes("add-child")) {
        setError(
          "У вас уже есть аккаунт с этим email. Войдите и добавьте ребёнка через кабинет."
        );
      } else {
        setError(typeof msg === "string" ? msg : "Ошибка регистрации");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      <PublicHeader />
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white p-8 border border-[var(--color-line)]">
          <h1
            className="text-3xl mb-2"
            style={{ fontFamily: "var(--font-serif)" }}
          >
            Регистрация
          </h1>
          <p className="text-[var(--color-muted)] mb-6">Гордость 94</p>

          <ErrorBanner message={error} />

          {step === 1 && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                performCheckCode(code);
              }}
            >
              <Input
                label="Код от классного руководителя"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="ABCD1234"
                maxLength={8}
                pattern="[A-HJ-NP-Z2-9]{8}"
                hint="8 символов без 0, O, 1, I"
                required
                autoFocus
              />
              <Button
                type="submit"
                variant="primary"
                loading={loading}
                className="w-full"
              >
                Проверить код
              </Button>
              <div className="mt-4 text-center text-sm">
                <Link to="/login" className="text-[var(--color-accent)]">
                  Уже есть аккаунт? Войти
                </Link>
              </div>
            </form>
          )}

          {step === 2 && studentInfo && (
            <form onSubmit={onRegister}>
              <div className="bg-[var(--color-paper-2)] p-4 mb-6 border-l-4 border-[var(--color-accent-2)]">
                <p className="text-sm text-[var(--color-muted)]">
                  Вы регистрируете кабинет для:
                </p>
                <p className="text-lg font-bold mt-1">
                  {studentInfo.student_full_name}
                </p>
                <p className="text-sm">
                  {studentInfo.student_class} · {studentInfo.student_year}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setStep(1);
                    setStudentInfo(null);
                  }}
                  className="text-xs text-[var(--color-accent)] mt-2 underline"
                >
                  Это не мой ребёнок
                </button>
              </div>

              <Input
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
              <Input
                label="Пароль"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                hint="Мин. 8 символов, обязательно цифра"
                autoComplete="new-password"
              />
              <Input
                label="Повторите пароль"
                type="password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                required
                autoComplete="new-password"
              />
              <Input
                label="Ваше имя"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Иванова Мария Петровна"
                required
                autoComplete="name"
              />
              <Input
                label="Телефон (опц.)"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="tel"
              />

              <label className="flex items-start gap-3 mt-4 mb-6 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consentPdn}
                  onChange={(e) => setConsentPdn(e.target.checked)}
                  className="mt-1"
                  required
                />
                <span className="text-sm">
                  Я согласен на обработку персональных данных моего ребёнка{" "}
                  <button
                    type="button"
                    onClick={() => setConsentModalOpen(true)}
                    className="text-[var(--color-accent)] underline"
                  >
                    (полный текст)
                  </button>
                </span>
              </label>

              <Button
                type="submit"
                variant="primary"
                loading={loading}
                disabled={!consentPdn}
                className="w-full"
              >
                Зарегистрироваться
              </Button>
            </form>
          )}

          <ConsentModal
            open={consentModalOpen}
            onClose={() => setConsentModalOpen(false)}
            studentName={studentInfo?.student_full_name}
            studentYear={studentInfo?.student_year}
          />
        </div>
      </main>
    </div>
  );
}
